import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import { useAppStore } from '../store/appStore';
import BranchSelector from '../components/common/BranchSelector';
import lyraposLogo from '../assets/images/lyrapos-logo.svg';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  CssBaseline,
  IconButton,
  Avatar,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
  Chip,
  Menu,
  MenuItem,
  Collapse,
  Badge,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Store as StoreIcon,
  Business as BusinessIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  BarChart as AnalyticsIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Close as CloseIcon,
  Restaurant as RestaurantIcon,
  Group as GroupIcon,
  Warehouse as WarehouseIcon,
} from "@mui/icons-material";

const drawerWidth = 280;

export const AdminLayout = ({ onNotification }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileOpen, setMobileOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const { user, logout } = useAuth();
  const { branch: selectedBranch } = useAppStore();

  // Define menu items based on user role
  const superAdminMenuItems = [
    { text: "Dashboard", icon: <DashboardIcon />, path: "/super-admin/dashboard", adminOnly: false },
    { text: "Vendors", icon: <BusinessIcon />, path: "/vendors", adminOnly: true },
    { text: "Reports", icon: <AnalyticsIcon />, path: "/reports", adminOnly: false },
  ];

  // Generate vendor menu items based on selected branch
  const getVendorMenuItems = () => {
    const isAllOutlets = selectedBranch?.isAllOutlets || selectedBranch?.id === 'all-outlets';
    
    const baseMenuItems = [
      { text: "Dashboard", icon: <DashboardIcon />, path: "/vendor/dashboard", adminOnly: false },
      { text: "Reports", icon: <AnalyticsIcon />, path: "/reports", adminOnly: false },
    ];

    // Management items depend on branch selection
    const managementItems = isAllOutlets
      ? [{ text: "Branches", icon: <WarehouseIcon />, path: "/branches", adminOnly: false }]
      : [
          { text: "Menu", icon: <RestaurantIcon />, path: "/dish-access", adminOnly: false },
          { text: "Users", icon: <GroupIcon />, path: "/users", adminOnly: false },
        ];

    return [...baseMenuItems, ...managementItems];
  };

  const vendorMenuItems = getVendorMenuItems();

  // Filter menu based on profile role
  const filteredMenuItems = user
    ? user.role === 'superadmin'
      ? superAdminMenuItems
      : vendorMenuItems
    : vendorMenuItems;

  // Online/Offline status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Expand reports submenu automatically when path starts with /reports
  useEffect(() => {
    if (location.pathname.startsWith('/reports')) {
      setReportsOpen(true);
    }
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const showNotification = (message, severity = "info") => {
    if (onNotification) {
      onNotification(message, severity);
    } else {
      setNotification({ open: true, message, severity });
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleNavigation = (path) => {
    navigate(path);
    // Only close drawer on mobile screens
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Helper rendering logic for two-section sidebar ('Menu' and 'Management')
  const menuSections = [
    {
      title: 'Menu',
      items: filteredMenuItems.filter((item) => ['Dashboard', 'Reports'].includes(item.text)),
    },
    {
      title: 'Management',
      items: filteredMenuItems.filter((item) => !['Dashboard', 'Reports'].includes(item.text)),
    },
  ];

  const renderMenuSections = () => {
    return menuSections.map((section) => {
      if (!section.items || section.items.length === 0) {
        return null;
      }

      return (
        <Box key={section.title} sx={{ mb: 2 }}>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              px: 2,
              py: 1,
              color: 'text.secondary',
              fontSize: '0.75rem',
              letterSpacing: '0.08rem',
            }}
          >
            {section.title}
          </Typography>
          <List disablePadding>
            {section.items.map((item) => {
              const itemSelected = item.children
                ? location.pathname.startsWith(item.path)
                : location.pathname === item.path;

              return (
                <React.Fragment key={item.text}>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={itemSelected}
                      onClick={() => {
                        if (item.children) {
                          setReportsOpen((prev) => !prev);
                        } else {
                          handleNavigation(item.path);
                        }
                      }}
                      sx={{
                        borderRadius: 2,
                        py: 1.25,
                        mx: 1,
                        '&.Mui-selected': {
                          bgcolor: theme.palette.primary.main,
                          color: 'white',
                          '&:hover': {
                            bgcolor: theme.palette.primary.dark,
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: itemSelected ? 'white' : 'inherit',
                          minWidth: 40,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontWeight: itemSelected ? 600 : 400,
                          fontSize: '0.95rem',
                        }}
                      />
                      {item.children && (reportsOpen ? <ExpandLess /> : <ExpandMore />)}
                    </ListItemButton>
                  </ListItem>
                  {item.children && (
                    <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.children.map((sub) => (
                          <ListItemButton
                            key={sub.text}
                            sx={{ pl: 5, py: 1, borderRadius: 2, mb: 0.75, mx: 1 }}
                            selected={location.pathname === sub.path}
                            onClick={() => handleNavigation(sub.path)}
                          >
                            <ListItemText
                              primary={sub.text}
                              primaryTypographyProps={{
                                fontWeight: location.pathname === sub.path ? 600 : 400,
                                fontSize: '0.9rem',
                              }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      );
    });
  };

  const drawer = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 2, position: "relative" }}>
        <Box
          component="img"
          src={lyraposLogo}
          alt="LyraPOS Logo"
          sx={{
            width: 42,
            height: 42,
            borderRadius: 1.5,
            objectFit: 'contain',
            backgroundColor: 'white',
            p: 0.5,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
            LyraPOS
          </Typography>
          {user && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user.email || user.name || (user.role === 'superadmin' ? 'Super Admin' : 'Vendor')}
            </Typography>
          )}
        </Box>
        {/* Close button for mobile drawer */}
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            display: { xs: "flex", sm: "none" },
            position: "absolute",
            top: 8,
            right: 8,
            color: "text.secondary",
            "&:hover": {
              color: "text.primary",
              bgcolor: "action.hover",
            },
          }}
          aria-label="close drawer"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mx: 2 }} />

      <Box sx={{ px: 2, mt: 2 }}>
        {renderMenuSections()}
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      {/* User info in footer */}
      
    </Box>
  );

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <CssBaseline />

      <Box component="nav" sx={{ width: { sm: mobileOpen ? drawerWidth : 0 }, flexShrink: { sm: 0 }, transition: "width 0.3s ease-in-out" }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: "1px solid #e0e0e0",
              boxShadow: "none",
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: mobileOpen ? drawerWidth : 0,
              borderRight: mobileOpen ? "1px solid #e0e0e0" : "none",
              boxShadow: "none",
              transition: "width 0.3s ease-in-out",
              overflow: "hidden",
            },
          }}
          open={mobileOpen}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { sm: mobileOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          bgcolor: '#f5f5f5',
          transition: 'width 0.3s ease-in-out',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          height: '100vh',
        }}
      >
        <AppBar
          position="static"
          color="default"
          sx={{
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #e0e0e0',
            mb: 0,
            py: 0,
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between", px: { xs: 1.5, sm: 2, md: 3 }, py: 1, minHeight: 'auto' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1.5 }}
              size="large"
            >
              <MenuIcon />
            </IconButton>

            {/* Display user name on the left */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'text.secondary',
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                Welcome, {user?.full_name || user?.email || 'User'}
              </Typography>
            </Box>

            <Typography
              variant="h6"
              component="div"
              sx={{
                display: { xs: "none", md: "block" },
                fontWeight: 600,
                fontSize: "1.1rem",
                flex: 1,
                textAlign: "center",
              }}
            >
              {filteredMenuItems.find((item) => item.path === location.pathname)?.text || "Dashboard"}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexShrink: 0 }}>
              {/* Branch Selector - only show for vendor role */}
              {user?.role === 'vendor' && <BranchSelector />}

              <Tooltip title="Notifications">
                <IconButton size="large" color="inherit">
                  <NotificationsIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Your Profile">
                <IconButton
                  size="large"
                  edge="end"
                  color="inherit"
                  onClick={handleAvatarClick}
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                    {user?.full_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </Avatar>
                </IconButton>
              </Tooltip>

              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2">{user?.email ?? "User"}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.full_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {user?.role === 'superadmin' ? 'Super Admin' : 'Vendor'}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem onClick={() => { handleCloseMenu(); handleLogout(); }}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Logout
                </MenuItem>
              </Menu>

              <Chip
                icon={isOnline ? <WifiIcon /> : <WifiOffIcon />}
                label={isOnline ? "Online" : "Offline"}
                color={isOnline ? "success" : "error"}
                variant="outlined"
                size="small"
                sx={{ display: { xs: 'none', md: 'inline-flex' } }}
              />
            </Box>
          </Toolbar>
        </AppBar>

        <Box 
          sx={{ 
            flex: 1,
            overflowY: 'auto', 
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Outlet />
        </Box>
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{
            borderRadius: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
